import { createContext, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { ec as EC } from "elliptic";

import { auth, db } from "../firebaseConfig";
import toast from "react-hot-toast";

const UserContext = createContext();

export const AuthContextProvider = ({ children }) => {
  const [user, setUser] = useState({});
  const [userData, setUserData] = useState(null);

  const generateWalletAddress = () => {
    // Example implementation using a simple random string generator
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const walletAddressLength = 42; // Ethereum wallet address length
    let walletAddress = "0x";
    for (let i = 0; i < walletAddressLength - 2; i++) {
      walletAddress += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return walletAddress;
  };
  const generatePrivateKey = () => {
    // Use Web Crypto API to generate a Uint8Array of 32 random bytes
    const randomValues = window.crypto.getRandomValues(new Uint8Array(32));
    // Convert each byte to a two-character hexadecimal number and concatenate them
    const privateKey =
      "0x" +
      Array.from(randomValues)
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    return privateKey;
  };

  const generatePublicKey = (privateKey) => {
    const ec = new EC("secp256k1");
    const keyPair = ec.keyFromPrivate(privateKey.slice(2), "hex");
    return "0x" + keyPair.getPublic(false, "hex").slice(2);
  };

  const signUp = async (email, password) => {
    try {
      // Generate wallet address, private key, public key
      const walletAddress = generateWalletAddress();
      const privateKey = generatePrivateKey();
      const publicKey = generatePublicKey(privateKey);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const { user } = userCredential;

      const newUserData = {
        id: user.uid,
        email: user.email,
        isWalletCreated: false,
        walletAddress,
        privateKey,
        publicKey,
        balance: 10,
        joinedAt: new Date(),
        createdAt: new Date(),
      };

      // Store only the authenticated user data in Firestore
      await db.collection("users").doc(user.email).set(newUserData);
      setUser(user);
      setUserData(newUserData);
      return user; // Return only the user object
    } catch (error) {
      console.error(error.message);
      throw error;
    }
  };

  const signIn = async (email, password) => {
    try {
      const userCrenditals = await signInWithEmailAndPassword(auth, email, password);
      const { user } = userCrenditals;
      setUser(user);
      return user;
    } catch (error) {
      console.log(error);
      console.log(error.code);
      console.error("Error signing in");
      toast.error("Error signing in");
      throw error;
    }
  };

  const logout = () => {
    return signOut(auth);
  };

  useEffect(() => {
    let unsubscribeUserDoc = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);

      if (unsubscribeUserDoc) {
        unsubscribeUserDoc();
        unsubscribeUserDoc = null;
      }

      if (currentUser?.email) {
        // Use onSnapshot to listen for real-time updates to the user's document
        unsubscribeUserDoc = db
          .collection("users")
          .doc(currentUser.email)
          .onSnapshot(
            (doc) => {
              if (doc.exists) {
                setUserData(doc.data());
              } else {
                setUserData(null);
              }
            },
            (error) => {
              console.error("Error fetching user data:", error.message);
            }
          );
      } else {
        setUserData(null);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeUserDoc) {
        unsubscribeUserDoc();
      }
    };
  }, []);

  return (
    <UserContext.Provider value={{ signUp, user, logout, signIn, userData, setUserData }}>
      {children}
    </UserContext.Provider>
  );
};

export const UserAuth = () => {
  return useContext(UserContext);
};
